import React from 'react';
import { Target, Eye, MessageSquare, TrendingUp } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About <span className="text-cyan-400">FutureBuildX</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Building an innovation ecosystem for the next generation of technology leaders
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Vision */}
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-10 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-cyan-400" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Vision</h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                To be the leading force in transforming technology education across the state, creating a generation
                of innovators who are equipped with practical skills, creative thinking, and the confidence to build
                solutions that shape the future.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-10 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-6">
                <Target className="text-cyan-400" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                To empower school and college students with hands-on training in AI, Robotics, and IoT through
                practical workshops, expert guidance, and industry-oriented projects that bridge the gap between
                academic learning and real-world application.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-6">Our Story</h2>
          </div>
          <div className="space-y-6 text-lg text-gray-400 leading-relaxed">
            <p>
              FutureBuildX was born from a simple observation: students across our state had immense potential and
              curiosity about emerging technologies, but lacked access to quality practical training and guidance.
              Traditional education systems focused heavily on theory, leaving a significant gap in hands-on experience
              with technologies that are shaping our world.
            </p>
            <p>
              We started with a mission to change this narrative. By bringing together passionate educators, industry
              experts, and technology enthusiasts, we created a platform where students could not just learn about
              technology, but actually build with it. From conducting our first robotics workshop with 20 students to
              now reaching thousands across the state, our journey has been driven by the success stories of young
              innovators we've helped create.
            </p>
            <p>
              Today, FutureBuildX stands as a trusted partner for schools and colleges, providing comprehensive training
              programs, project guidance, and technical services that enable students to transform their ideas into
              reality. Every workshop we conduct, every project we guide, and every service we provide is a step towards
              building the innovation ecosystem of tomorrow.
            </p>
          </div>
        </div>
      </section>

      {/* Founder's Message */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-10 md:p-12">
            <div className="flex items-center space-x-3 mb-6">
              <MessageSquare className="text-cyan-400" size={32} />
              <h2 className="text-3xl font-bold text-white">Founder's Message</h2>
            </div>
            <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
              <p>
                "When we started FutureBuildX, we had one clear goal: to make cutting-edge technology education
                accessible and practical for every student who has the curiosity to learn and innovate."
              </p>
              <p>
                "I've witnessed firsthand how the right guidance and hands-on experience can transform a student's
                confidence and capabilities. It's incredibly fulfilling to see young minds grasp complex concepts,
                build functioning prototypes, and develop solutions to real-world problems."
              </p>
              <p>
                "As we continue to grow, our commitment remains unchanged - to create an environment where every student
                feels empowered to explore, experiment, and excel in the world of technology. The future is being built
                by these young innovators, and we're honored to be part of their journey."
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-6 mt-6 border-t border-cyan-500/20">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-md"></div>
                    <img
                      src="/founder-darshan.jpeg"
                      alt="Darshan Hadole - Founder, FutureBuildX"
                      data-testid="founder-photo"
                      className="relative w-20 h-20 rounded-full object-cover border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/20"
                    />
                  </div>
                  <div>
                    <p className="text-cyan-400 font-semibold text-xl">Darshan Hadole</p>
                    <p className="text-gray-400 text-sm">Founder, FutureBuildX</p>
                  </div>
                </div>
                <a
                  href="https://www.linkedin.com/feed/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 sm:mt-0 inline-flex items-center space-x-2 px-5 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-500/50 text-cyan-400 rounded-lg transition-all duration-300 group"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <span className="font-medium">Connect on LinkedIn</span>
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Co-Founder's Message */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-10 md:p-12">
            <div className="flex items-center space-x-3 mb-6">
              <MessageSquare className="text-cyan-400" size={32} />
              <h2 className="text-3xl font-bold text-white">Co-Founder's Message</h2>
            </div>
            <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
              <p>
                "When we started FutureBuildX, our vision was simple — to make technology education more practical,
                accessible, and connected to real-world applications. We wanted to create a platform where students
                could move beyond learning concepts and actually build, experiment, and innovate."
              </p>
              <p>
                "As a Co-Founder, I have seen how hands-on experience can transform the way students learn and solve
                problems. Working with technologies such as AI, Machine Learning, Robotics, IoT, Embedded Systems,
                and 3D Printing gives students the opportunity to turn their ideas into meaningful, working solutions."
              </p>
              <p>
                "At FutureBuildX, we focus on creating opportunities for students to learn by doing. Through workshops,
                internships, project guidance, and real-world projects, we aim to help students develop technical
                skills, creativity, confidence, and problem-solving abilities."
              </p>
              <p>
                "Our goal is not just to teach emerging technologies, but to build a community of young innovators who
                are willing to learn, experiment, and create. We believe the future is built by those who take ideas
                beyond imagination and turn them into reality."
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-6 mt-6 border-t border-cyan-500/20">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-md"></div>
                    <img
                      src="/cofounder-shubham.jpeg"
                      alt="Shubham Kungar - Co-Founder, FutureBuildX"
                      data-testid="cofounder-photo"
                      className="relative w-20 h-20 rounded-full object-cover border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/20"
                    />
                  </div>
                  <div>
                    <p className="text-cyan-400 font-semibold text-xl">Shubham Kungar</p>
                    <p className="text-gray-400 text-sm">Co-Founder, FutureBuildX</p>
                  </div>
                </div>
                <a
                  href="https://www.linkedin.com/in/shubhamkungar515"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cofounder-linkedin-btn"
                  className="mt-4 sm:mt-0 inline-flex items-center space-x-2 px-5 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-500/50 text-cyan-400 rounded-lg transition-all duration-300 group"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <span className="font-medium">Connect on LinkedIn</span>
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Goals */}
      <section className="py-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 mb-6">
              <TrendingUp className="text-cyan-400" size={36} />
              <h2 className="text-4xl font-bold text-white">Future Goals</h2>
            </div>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Our roadmap for expanding impact and building a stronger innovation ecosystem
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4 text-cyan-400 font-bold text-xl">
                01
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Expand State Presence</h3>
              <p className="text-gray-400">
                Reach 500+ educational institutions across the state with our workshops and training programs.
              </p>
            </div>

            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4 text-cyan-400 font-bold text-xl">
                02
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Innovation Labs</h3>
              <p className="text-gray-400">
                Establish dedicated innovation labs in partner institutions for continuous learning and experimentation.
              </p>
            </div>

            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4 text-cyan-400 font-bold text-xl">
                03
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Industry Partnerships</h3>
              <p className="text-gray-400">
                Collaborate with leading tech companies to provide internship and placement opportunities for students.
              </p>
            </div>

            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4 text-cyan-400 font-bold text-xl">
                04
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Advanced Certifications</h3>
              <p className="text-gray-400">
                Launch industry-recognized certification programs in AI, Robotics, and IoT technologies.
              </p>
            </div>

            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4 text-cyan-400 font-bold text-xl">
                05
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Online Learning Platform</h3>
              <p className="text-gray-400">
                Develop a comprehensive online platform for remote learning and project collaboration.
              </p>
            </div>

            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4 text-cyan-400 font-bold text-xl">
                06
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Innovation Competitions</h3>
              <p className="text-gray-400">
                Organize annual state-level technology competitions to showcase student innovations and talents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Join Us in Building the Future
          </h2>
          <p className="text-lg text-gray-400 mb-8">
            Partner with FutureBuildX to transform technology education in your institution
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transform hover:scale-105 transition-all duration-300 shadow-lg shadow-cyan-500/30"
          >
            Get Started Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
